/**
 * GLSL for the narrative backdrop's planet (`PlanetStage.ts`). Two kinds of shader:
 *
 *  - **Bake** shaders run once, into equirectangular render targets: the surface (albedo + an ocean
 *    mask in alpha) and the cloud cover. Noise is sampled on the unit sphere, so the maps have no
 *    seam, and the per-frame cost is a texture read instead of a dozen octaves of noise per pixel.
 *  - **Frame** shaders light those maps every frame: a soft terminator from the key light, ocean
 *    glint, drifting cloud bands with their shadows, and a thin Fresnel atmosphere.
 *
 * The palette is the codex's survey fact made visible: a terraformed Carboniferous world, moss and
 * prehistoric forest over the land, teal and verdigris algae-bloom seas, a thin new atmosphere.
 */

/** Ashima Arts' 3D simplex noise (MIT, github.com/ashima/webgl-noise) and an fbm over it. */
export const NOISE = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.5 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 105.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}
float fbm(vec3 p, int octaves) {
  float sum = 0.0, amp = 0.5;
  for (int i = 0; i < 9; i++) {
    if (i >= octaves) break;
    sum += amp * snoise(p);
    p = p * 2.03 + vec3(1.7, -3.1, 5.3);
    amp *= 0.5;
  }
  return sum;
}
/** The unit-sphere point a SphereGeometry UV maps to (three's own parametrisation), so maps are seamless. */
vec3 spherePoint(vec2 uv) {
  float phi = uv.x * 6.28318530718;
  float theta = (1.0 - uv.y) * 3.14159265359;
  return vec3(-cos(phi) * sin(theta), cos(theta), sin(phi) * sin(theta));
}
`;

/** A full-screen triangle for the bakes. */
export const BAKE_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** Surface albedo in rgb, ocean mask in alpha. `uSeed` shifts the noise field (one world, one seed). */
export const SURFACE_BAKE_FRAGMENT = /* glsl */ `
uniform vec3 uSeed;
varying vec2 vUv;
${NOISE}
void main() {
  vec3 p = spherePoint(vUv) + uSeed;
  // Continents: warped fbm, so coastlines curl instead of blobbing.
  vec3 warp = vec3(fbm(p * 1.1, 4), fbm(p * 1.1 + 7.3, 4), fbm(p * 1.1 - 4.1, 4));
  float c = fbm(p * 1.35 + warp * 0.9, 8);
  float sea = 0.02;
  float e = c - sea;
  float land = smoothstep(-0.004, 0.012, e);

  // Land: moss lowlands, darker fern forest in broad patches, olive lichen uplands, rock on the peaks.
  float forest = smoothstep(-0.1, 0.2, fbm(p * 4.5 + warp * 1.4, 5));
  float detail = fbm(p * 22.0, 4);
  vec3 moss = vec3(0.22, 0.34, 0.11);
  vec3 fern = vec3(0.07, 0.19, 0.08);
  vec3 lichen = vec3(0.34, 0.42, 0.14);
  vec3 rock = vec3(0.33, 0.33, 0.26);
  vec3 ground = mix(moss, fern, forest);
  ground = mix(ground, lichen, smoothstep(0.24, 0.38, e + detail * 0.05) * 0.8);
  ground = mix(ground, rock, smoothstep(0.5, 0.64, e + detail * 0.06));
  ground *= 0.85 + 0.3 * detail;
  // Swampy coastal margin: wetter, darker green.
  ground = mix(vec3(0.10, 0.24, 0.12), ground, smoothstep(0.0, 0.04, e));

  // Seas: deep teal, verdigris shallows, algae-bloom filaments drawn along a warped flow field.
  vec3 deep = vec3(0.02, 0.15, 0.17);
  vec3 shelf = vec3(0.10, 0.40, 0.36);
  vec3 bloomGreen = vec3(0.12, 0.34, 0.20);
  vec3 bloomBright = vec3(0.30, 0.66, 0.52);
  vec3 water = mix(deep, shelf, smoothstep(-0.08, 0.0, e) * 0.85);
  vec3 q = p * 2.6 + warp * 2.2;
  float flow = fbm(q + vec3(fbm(q * 1.7, 4) * 1.6), 6);
  float bloom = smoothstep(0.0, 0.35, flow) * smoothstep(-0.32, -0.03, e);
  water = mix(water, bloomGreen, bloom * 0.75);
  float filament = smoothstep(0.03, 0.0, abs(flow - 0.16)) * smoothstep(-0.3, -0.04, e);
  water = mix(water, bloomBright, filament * 0.55);

  vec3 col = mix(water, ground, land);
  // Thin frost caps: a young atmosphere, not yet an ice age.
  float lat = abs(spherePoint(vUv).y);
  float cap = smoothstep(0.90, 0.96, lat + fbm(p * 6.0, 3) * 0.05);
  col = mix(col, vec3(0.78, 0.86, 0.84), cap * 0.85);

  gl_FragColor = vec4(col, (1.0 - land) * (1.0 - cap));
}
`;

/** Cloud cover in red: a few long latitudinal bands with storm knots, mostly clear between. */
export const CLOUD_BAKE_FRAGMENT = /* glsl */ `
uniform vec3 uSeed;
varying vec2 vUv;
${NOISE}
void main() {
  vec3 s = spherePoint(vUv);
  vec3 p = s + uSeed * 1.7;
  float lat = s.y;
  // Bands: stretched noise along longitude, gated to a handful of latitude belts.
  float belts = smoothstep(0.35, 0.95, 0.5 + 0.5 * sin(lat * 8.0 + 0.6 + fbm(p * 1.2, 3) * 1.6));
  vec3 stretched = vec3(p.x * 1.4, p.y * 11.0, p.z * 1.4);
  float streak = fbm(stretched + fbm(p * 3.0, 4) * 0.9, 7);
  float d = smoothstep(-0.05, 0.4, streak) * belts;
  float knots = smoothstep(0.32, 0.62, fbm(p * 3.4, 6));
  d = max(d, knots * 0.6);
  d *= 0.75 + 0.25 * fbm(p * 18.0, 3);
  gl_FragColor = vec4(clamp(d, 0.0, 1.0), 0.0, 0.0, 1.0);
}
`;

/** Shared vertex for the lit spheres: view-space normal and position. */
export const SPHERE_VERTEX = /* glsl */ `
varying vec2 vUv;
varying vec3 vNormalV;
varying vec3 vPosV;
void main() {
  vUv = uv;
  vNormalV = normalize(normalMatrix * normal);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vPosV = mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;

/** The planet itself, lit every frame. `uLight` is the key light's direction in view space. */
export const PLANET_FRAGMENT = /* glsl */ `
uniform sampler2D uSurface;
uniform sampler2D uClouds;
uniform vec3 uLight;
uniform float uCloudShift;
uniform vec3 uAtmo;
varying vec2 vUv;
varying vec3 vNormalV;
varying vec3 vPosV;
void main() {
  vec3 n = normalize(vNormalV);
  vec3 v = normalize(-vPosV);
  vec3 l = normalize(uLight);
  vec4 surf = texture2D(uSurface, vUv);
  vec2 cuv = vec2(vUv.x + uCloudShift, vUv.y);
  float cloud = texture2D(uClouds, cuv).r;
  // The shadow a cloud casts lands a little away from the light.
  float shade = texture2D(uClouds, cuv + vec2(0.0035, -0.0025)).r;

  float ndl = dot(n, l);
  float lambert = max(ndl, 0.0);
  float day = smoothstep(-0.18, 0.32, ndl);

  vec3 albedo = surf.rgb * (1.0 - 0.5 * shade * day);
  vec3 col = albedo * (lambert * 1.35 + 0.035);
  // Warm scattering at the terminator.
  col += albedo * vec3(0.55, 0.28, 0.12) * smoothstep(0.25, 0.0, abs(ndl - 0.02)) * 0.35;

  // Ocean glint.
  vec3 h = normalize(l + v);
  float spec = pow(max(dot(n, h), 0.0), 70.0) * surf.a * day;
  col += vec3(0.75, 0.92, 0.85) * spec * 0.55;

  // Clouds, lit on their own (brighter than the ground, a hint of blue in shadow).
  vec3 cloudCol = vec3(0.93, 0.97, 0.95) * (lambert * 1.25 + 0.012) + vec3(0.004, 0.012, 0.014);
  col = mix(col, cloudCol, cloud * 0.88);

  // The thin new atmosphere: Fresnel haze, strongest where it is lit.
  float fres = pow(1.0 - max(dot(n, v), 0.0), 2.6);
  col += uAtmo * fres * (0.12 + 1.1 * smoothstep(-0.25, 0.7, ndl));
  // A faint teal night-side airglow so the dark limb still reads.
  col += uAtmo * 0.05 * fres * (1.0 - day);

  gl_FragColor = vec4(col, 1.0);
}
`;

/** The atmosphere shell: a back-faced sphere a touch larger, added over the planet's silhouette. */
export const ATMOSPHERE_FRAGMENT = /* glsl */ `
uniform vec3 uLight;
uniform vec3 uAtmo;
varying vec2 vUv;
varying vec3 vNormalV;
varying vec3 vPosV;
void main() {
  vec3 n = normalize(vNormalV);
  vec3 v = normalize(-vPosV);
  // Back faces: the normal points away; the glow peaks where it grazes the view.
  float rim = pow(clamp(1.0 + dot(n, v) * 1.15, 0.0, 1.0), 3.2);
  float lit = smoothstep(-0.35, 0.6, dot(n, normalize(uLight)));
  vec3 col = uAtmo * rim * (0.08 + 1.25 * lit);
  gl_FragColor = vec4(col, 1.0);
}
`;

/** Orbital traffic and stars: round soft points, sized in pixels, with a per-point brightness. */
export const POINTS_VERTEX = /* glsl */ `
attribute float aSize;
attribute float aGlow;
uniform float uPixelRatio;
varying float vGlow;
void main() {
  vGlow = aGlow;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * uPixelRatio;
  gl_Position = projectionMatrix * mv;
}
`;

/** Orbital traffic: each dot runs its own circular orbit, computed here from its lane and phase. */
export const TRAFFIC_VERTEX = /* glsl */ `
attribute float aAngle;
attribute float aRadius;
attribute float aLift;
attribute float aSize;
attribute float aGlow;
uniform float uTime;
uniform float uPixelRatio;
varying float vGlow;
void main() {
  // Inner lanes run faster (Kepler, loosely); slow enough to read as traffic, not a spinner.
  float a = aAngle + uTime * 0.05 / pow(aRadius, 1.5);
  vec3 p = vec3(cos(a) * aRadius, aLift, sin(a) * aRadius);
  vGlow = aGlow * (0.7 + 0.3 * sin(uTime * 2.3 + aAngle * 37.0));
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_PointSize = aSize * uPixelRatio;
  gl_Position = projectionMatrix * mv;
}
`;

export const POINTS_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
varying float vGlow;
void main() {
  vec2 d = gl_PointCoord - 0.5;
  float a = smoothstep(0.5, 0.0, length(d));
  gl_FragColor = vec4(uColor * vGlow * a, 1.0);
}
`;
